import { faArrowRotateLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

import { useCardOrder } from '@/contexts/cardOrderContext';
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator } from '@/ui';

import './styles.css';

/**
 * The card order choices, without a menu around them.
 *
 * They are the toolbar's own menu when there is room for its button, and part
 * of the overflow menu when there is not, so they are written once here rather
 * than kept in step in two places.
 */
const CardOrderMenuItems = () => {
  const { controls } = useCardOrder();

  if (!controls?.isAvailable) return null;

  const {
    applyRoleOrder,
    canEdit,
    editRole,
    editingRole,
    hasPersonalOrder,
    isDirty,
    isSaving,
    matchingRole,
    myRoles,
    resetToRoleDefault,
    roles
  } = controls;

  // Switching roles would throw an unsaved draft away, so the other roles wait
  // until it has been saved or discarded. A disabled menu item still fires its
  // click handler, so the guard is on the action rather than the markup alone.
  const isRoleLocked = (slug: string) => isSaving || (isDirty && slug !== editingRole);

  // While a role's default is being drafted the check follows the draft;
  // otherwise it marks whichever layout the page is actually using.
  const isChecked = (slug: string) => (editingRole ? slug === editingRole : slug === matchingRole);

  return (
    <>
      <DropdownMenuGroup label="Card Order">
        {canEdit
          ? roles.map(({ name, slug }) => (
              <DropdownMenuItem
                disabled={isRoleLocked(slug)}
                icon={isChecked(slug) ? faCheck : undefined}
                key={slug}
                onClick={() => !isRoleLocked(slug) && editRole(slug)}>
                {name}
              </DropdownMenuItem>
            ))
          : myRoles.map(({ name, slug }) => (
              <DropdownMenuItem
                icon={isChecked(slug) ? faCheck : undefined}
                key={slug}
                onClick={() => applyRoleOrder(slug)}>
                {name}
              </DropdownMenuItem>
            ))}
      </DropdownMenuGroup>

      {(editingRole || hasPersonalOrder) && <DropdownMenuSeparator />}

      {editingRole && (
        <DropdownMenuItem disabled={isDirty} onClick={() => !isDirty && editRole(null)}>
          Back to my order
        </DropdownMenuItem>
      )}

      {hasPersonalOrder && (
        <DropdownMenuItem icon={faArrowRotateLeft} onClick={resetToRoleDefault}>
          Reset to my role&rsquo;s default
        </DropdownMenuItem>
      )}
    </>
  );
};

export default React.memo(CardOrderMenuItems);
